/**
 * Unified checkout upsell + buy-now flow (single module, no split imports).
 * checkout-v2026-07-12-en-sevilenler-cart
 */

/** @typedef {'modal' | 'cart'} UpsellView */
/** @typedef {{ redirect?: string, productId?: string, cartHandles?: string, cartIds?: string }} UpsellOpenDetail */

export const PINNED_UPSELL_MAP = {
  shogun1903: [
    { id: 8137023783021, handle: 'kartal-maskot' },
    { id: 8128774799469, handle: 'sghl225' },
  ],
  'aslan-figurlu-1905': [
    { id: 8137020309613, handle: 'aslan-maskot' },
    { id: 8128775389293, handle: 'sghl113' },
  ],
};

function getCartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      ids.push(item.dataset.sectionId);
    }
  });
  return [...new Set(ids)].join(',');
}

const PINNED_PRODUCT_CACHE = {
  8137023783021: {
    id: 8137023783021,
    title: 'KARTAL MASKOT FİGÜR',
    price: 72000,
    url: '/products/kartal-maskot',
    featured_image: 'https://cdn.shopify.com/s/files/1/0693/8800/9581/files/KARTALF.png?v=1782592401',
    variants: [{ id: 45388146901101, available: true }],
  },
  8128774799469: {
    id: 8128774799469,
    title: 'Beşiktaş 3D Dekoratif ay Gece Lambası Moon Lamp Pilli(13 Cm)',
    price: 71000,
    url: '/products/sghl225',
    featured_image:
      'https://cdn.shopify.com/s/files/1/0693/8800/9581/files/1_org_zoom_a561eda3-084d-4b08-87f1-a4865cc6ccd5.jpg?v=1781818364',
    variants: [{ id: 45354027745389, available: true }],
  },
  8137020309613: {
    id: 8137020309613,
    title: 'ASLAN TARAFTAR MASKOT FİGÜRÜ',
    price: 73000,
    url: '/products/aslan-maskot',
    featured_image: 'https://cdn.shopify.com/s/files/1/0693/8800/9581/files/leon3.png?v=1782591939',
    variants: [{ id: 45387988992109, available: true }],
  },
  8128775389293: {
    id: 8128775389293,
    title: 'Aslan Fotoğraflı Galatasaray 3D Dekoratif ay Gece Lambası Moon Lamp Pilli(13 Cm)',
    price: 75000,
    url: '/products/sghl113',
    featured_image:
      'https://cdn.shopify.com/s/files/1/0693/8800/9581/files/1_org_zoom_a5f1f5a9-80a1-4456-9328-ca5806b7cf80.jpg?v=1781818422',
    variants: [{ id: 45354028367981, available: true }],
  },
};

const TARAFTAR_COLLECTION = 'taraftar-urunleri';
/** Shopify handle: en-sevi + combining dot (U+0307) + lenler */
const EN_SEVILENLER_COLLECTION = 'en-sevi\u0307lenler';
const CART_DRAWER_UPSELL_LIMIT = 3;

const CATEGORY_COLLECTION_HANDLES = [
  TARAFTAR_COLLECTION,
  'gece-lambalari',
  'dekoratif-lambalar',
  'premium-figurler',
  'cerceve-ve-tablolar',
  'karakter-figurleri',
  'samdan-mumluk',
  'aplikler',
  'avizeler',
];

const TARAFTAR_UPSELL_LIMIT = 250;
const COLLECTION_PAGE_SIZE = 250;

const flowState = (window.__convCheckoutFlow ||= {
  openHandler: null,
  pendingOpens: [],
  listenersBound: false,
});

function normalizeHandle(handle) {
  return String(handle || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ı/g, 'i');
}

export function lookupPinnedForCartHandle(cartHandle) {
  if (PINNED_UPSELL_MAP[cartHandle]) return PINNED_UPSELL_MAP[cartHandle];
  const normalized = normalizeHandle(cartHandle);
  for (const [key, value] of Object.entries(PINNED_UPSELL_MAP)) {
    if (normalizeHandle(key) === normalized) return value;
  }
  return [];
}

export function hasPinnedRecommendations(cartHandlesCsv) {
  return hasUpsellRecommendations(cartHandlesCsv);
}

export function hasCartDrawerUpsell(cartIdsCsv = '') {
  const cartIds = new Set(cartIdsCsv.split(',').map((id) => id.trim()).filter(Boolean));
  const catalog = getEmbeddedEnSevilenlerCatalog();
  if (catalog.length > 0) {
    return catalog.some((product) => product?.id && !cartIds.has(String(product.id)));
  }
  return document.getElementById('conv-en-sevilenler-catalog') != null;
}

export function hasUpsellRecommendations(cartHandlesCsv, cartIdsCsv = '') {
  const cartHandles = cartHandlesCsv.split(',').map((h) => h.trim()).filter(Boolean);
  if (!cartHandles.length) return false;

  const { productRefs } = resolvePinnedProductRefs(cartHandlesCsv, cartIdsCsv);
  if (productRefs.length > 0) return true;

  const cartIds = new Set(cartIdsCsv.split(',').map((id) => id.trim()).filter(Boolean));
  const catalog = getEmbeddedTaraftarCatalog();
  if (catalog.length > 0) {
    return catalog.some((product) => product?.id && !cartIds.has(String(product.id)));
  }

  return false;
}

export function resolvePinnedProductRefs(cartHandlesCsv, cartIdsCsv = '') {
  const cartHandles = cartHandlesCsv.split(',').map((h) => h.trim()).filter(Boolean);
  const cartIds = new Set(cartIdsCsv.split(',').map((id) => id.trim()).filter(Boolean));
  const pinned = [];

  for (const handle of cartHandles) {
    for (const productRef of lookupPinnedForCartHandle(handle)) {
      const idStr = String(productRef.id);
      if (cartIds.has(idStr) || pinned.some((item) => item.id === productRef.id)) continue;
      pinned.push(productRef);
    }
  }

  return { productRefs: pinned, cartIds };
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatMoney(cents) {
  try {
    if (typeof window !== 'undefined' && typeof window.Shopify?.formatMoney === 'function') {
      const format =
        window.Theme?.moneyFormat ||
        window.Theme?.settings?.moneyFormat ||
        window.theme?.moneyFormat ||
        '{{amount}} TL';
      return window.Shopify.formatMoney(cents, format);
    }
  } catch {
    /* use fallback */
  }
  return `${(Number(cents) / 100).toFixed(2).replace('.', ',')} TL`;
}

function getPinnedProduct(ref) {
  return PINNED_PRODUCT_CACHE[ref.id] || null;
}

function normalizeTags(tags) {
  if (Array.isArray(tags)) return tags;
  if (typeof tags === 'string' && tags.trim()) {
    return tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }
  return [];
}

function normalizePrice(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  if (Number.isInteger(num) && num >= 1000) return num;
  if (String(value).includes('.')) return Math.round(num * 100);
  return num;
}

function normalizeProductFromJson(product) {
  if (!product?.id) return null;
  const variant = getAvailableVariant(product);
  const image = product.featured_image || product.images?.[0]?.src || product.images?.[0] || '';
  return {
    id: product.id,
    title: product.title || '',
    price: normalizePrice(variant?.price ?? product.price ?? 0),
    url: product.url || `/products/${product.handle || ''}`,
    featured_image: typeof image === 'string' ? image : image?.src || '',
    variants: product.variants || [],
    tags: normalizeTags(product.tags),
  };
}

function scoreProductRelevance(product, sourceTags) {
  let score = 0;
  for (const tag of product.tags || []) {
    if (sourceTags.has(tag)) score += 2;
    if (tag === TARAFTAR_COLLECTION) score += 3;
    if (CATEGORY_COLLECTION_HANDLES.includes(tag)) score += 1;
  }
  return score;
}

function getEmbeddedEnSevilenlerCatalog() {
  if (typeof window !== 'undefined' && window.__convEnSevilenlerCatalog) {
    return window.__convEnSevilenlerCatalog;
  }

  const script = document.getElementById('conv-en-sevilenler-catalog');
  if (!script?.textContent?.trim()) return [];

  try {
    const catalog = JSON.parse(script.textContent);
    if (typeof window !== 'undefined') {
      window.__convEnSevilenlerCatalog = catalog;
    }
    return Array.isArray(catalog) ? catalog : [];
  } catch {
    return [];
  }
}

function getEmbeddedTaraftarCatalog() {
  if (typeof window !== 'undefined' && window.__convTaraftarCatalog) {
    return window.__convTaraftarCatalog;
  }

  const script = document.getElementById('conv-taraftar-catalog');
  if (!script?.textContent?.trim()) return [];

  try {
    const catalog = JSON.parse(script.textContent);
    if (typeof window !== 'undefined') {
      window.__convTaraftarCatalog = catalog;
    }
    return Array.isArray(catalog) ? catalog : [];
  } catch {
    return [];
  }
}

function filterUpsellProducts(rawProducts, cartIds) {
  return rawProducts
    .map(normalizeProductFromJson)
    .filter((product) => product && !cartIds.has(String(product.id)));
}

async function fetchAllCollectionProducts(collectionHandle, cartIds, signal) {
  if (!collectionHandle) return [];
  const all = [];
  let page = 1;

  while (all.length < TARAFTAR_UPSELL_LIMIT) {
    try {
      const query =
        page === 1
          ? `limit=${COLLECTION_PAGE_SIZE}`
          : `limit=${COLLECTION_PAGE_SIZE}&page=${page}`;
      const response = await fetch(`/collections/${encodeURIComponent(collectionHandle)}/products.json?${query}`, {
        cache: 'no-store',
        signal,
      });
      if (!response.ok) break;
      const data = await response.json();
      const batch = data.products || [];
      if (!batch.length) break;

      for (const product of batch) {
        const normalized = normalizeProductFromJson(product);
        if (!normalized || cartIds.has(String(normalized.id))) continue;
        all.push(normalized);
        if (all.length >= TARAFTAR_UPSELL_LIMIT) return all;
      }

      if (batch.length < COLLECTION_PAGE_SIZE) break;
      page += 1;
    } catch {
      break;
    }
  }

  return all;
}

async function fetchCartDrawerUpsellProducts(cartIds, signal) {
  let products = filterUpsellProducts(getEmbeddedEnSevilenlerCatalog(), cartIds);

  if (!products.length) {
    products = await fetchAllCollectionProducts(EN_SEVILENLER_COLLECTION, cartIds, signal);
  }

  return products;
}

async function fetchTaraftarUpsellProducts(sourceProduct, cartIds, signal) {
  let products = filterUpsellProducts(getEmbeddedTaraftarCatalog(), cartIds);

  if (!products.length) {
    products = await fetchAllCollectionProducts(TARAFTAR_COLLECTION, cartIds, signal);
  }

  if (!products.length) return [];

  const sourceTags = new Set(normalizeTags(sourceProduct?.tags));

  return products.sort((a, b) => scoreProductRelevance(b, sourceTags) - scoreProductRelevance(a, sourceTags));
}

async function resolveSmartUpsellProducts(cartHandlesCsv, cartIdsCsv, signal) {
  const cartHandles = cartHandlesCsv.split(',').map((h) => h.trim()).filter(Boolean);
  const cartIds = new Set(cartIdsCsv.split(',').map((id) => id.trim()).filter(Boolean));

  const primaryHandle = pickPrimaryCartHandle(cartHandles);
  const primaryProduct = primaryHandle ? await fetchProductByHandle(primaryHandle, signal) : null;

  return fetchTaraftarUpsellProducts(primaryProduct, cartIds, signal);
}

function resolveCategoryHandle(product) {
  if (!product) return '';
  for (const tag of product.tags || []) {
    if (CATEGORY_COLLECTION_HANDLES.includes(tag)) return tag;
  }
  return '';
}

function pickPrimaryCartHandle(cartHandles) {
  if (!cartHandles.length) return '';
  return cartHandles[cartHandles.length - 1] || cartHandles[0];
}

async function fetchProductByHandle(handle, signal) {
  if (!handle) return null;
  try {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      cache: 'no-store',
      signal,
    });
    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
}

function getAvailableVariant(product) {
  return product.variants?.find((variant) => variant.available) || product.variants?.[0] || null;
}

function getFeaturedImage(product) {
  let image = product.featured_image || '';
  if (!image && product.images?.length) {
    const first = product.images[0];
    image = typeof first === 'string' ? first : first?.src || '';
  }
  if (!image) return '';
  return image.startsWith('//') ? `https:${image}` : image;
}

function renderModalProduct(product) {
  const variant = getAvailableVariant(product);
  const image = getFeaturedImage(product);
  const imageHtml = image
    ? `<img class="checkout-upsell-modal__img" loading="lazy" alt="${escapeHtml(product.title)}" src="${escapeHtml(image)}">`
    : '<span class="checkout-upsell-modal__img checkout-upsell-modal__img--placeholder"></span>';
  const actionHtml = variant?.available
    ? `<button type="button" class="checkout-upsell-modal__add button button--secondary" data-variant-id="${variant.id}" data-product-id="${product.id}">Sepete Ekle</button>`
    : '<span class="checkout-upsell-modal__soldout">Tükendi</span>';

  return `<li class="checkout-upsell-modal__item">
    <a href="${escapeHtml(product.url)}" class="checkout-upsell-modal__media">${imageHtml}</a>
    <div class="checkout-upsell-modal__details">
      <a href="${escapeHtml(product.url)}" class="checkout-upsell-modal__name">${escapeHtml(product.title.slice(0, 48))}</a>
      <span class="checkout-upsell-modal__price">${formatMoney(product.price)}</span>
    </div>
    ${actionHtml}
  </li>`;
}

function renderCartProduct(product) {
  const variant = getAvailableVariant(product);
  const image = getFeaturedImage(product);
  const imageHtml = image
    ? `<img class="cart-cross-sell__img" loading="lazy" alt="${escapeHtml(product.title)}" src="${escapeHtml(image)}">`
    : '<span class="cart-cross-sell__img cart-cross-sell__img--placeholder"></span>';
  const actionHtml = variant?.available
    ? `<button type="button" class="cart-cross-sell__add" data-variant-id="${variant.id}" data-product-id="${product.id}">Ekle</button>`
    : '<span class="cart-cross-sell__soldout">Tükendi</span>';

  return `<li class="cart-cross-sell__row">
    <a href="${escapeHtml(product.url)}" class="cart-cross-sell__media">${imageHtml}</a>
    <div class="cart-cross-sell__details">
      <a href="${escapeHtml(product.url)}" class="cart-cross-sell__name">${escapeHtml(product.title.slice(0, 42))}</a>
      <span class="cart-cross-sell__price">${formatMoney(product.price)}</span>
    </div>
    <div class="cart-cross-sell__action">${actionHtml}</div>
  </li>`;
}

function renderUpsellProducts(products, view) {
  if (!products.length) return '';

  if (view === 'cart') {
    return `<div class="cart-cross-sell-section cart-cross-sell-section--list">
      <h3 class="cart-cross-sell__title">Bunları da beğenebilirsiniz</h3>
      <ul class="cart-cross-sell__list" role="list">
        ${products.map(renderCartProduct).join('')}
      </ul>
    </div>`;
  }

  return `<div class="checkout-upsell-modal__section">
    <p class="checkout-upsell-modal__section-title">Taraftar Ürünleri (${products.length})</p>
    <ul class="checkout-upsell-modal__list" role="list">
      ${products.map(renderModalProduct).join('')}
    </ul>
  </div>`;
}

export async function fetchUpsellForCart(cartHandlesCsv, cartIdsCsv, view, signal) {
  try {
    let products;
    if (view === 'cart') {
      const cartIds = new Set(cartIdsCsv.split(',').map((id) => id.trim()).filter(Boolean));
      products = await fetchCartDrawerUpsellProducts(cartIds, signal);
      products = products.slice(0, CART_DRAWER_UPSELL_LIMIT);
    } else {
      products = await resolveSmartUpsellProducts(cartHandlesCsv, cartIdsCsv, signal);
    }
    return renderUpsellProducts(products, view);
  } catch (error) {
    console.warn('Checkout upsell render failed:', error);
    return '';
  }
}

export function fetchPinnedForCart(cartHandlesCsv, cartIdsCsv, view, signal) {
  return fetchUpsellForCart(cartHandlesCsv, cartIdsCsv, view, signal);
}

function isCheckoutUpsellEnabled() {
  return document.querySelector('checkout-upsell-modal[data-enabled="true"]') != null;
}

export function openCheckoutUpsell(detail = {}) {
  if (!isCheckoutUpsellEnabled()) {
    if (detail.redirect) window.location.replace(detail.redirect);
    return;
  }

  const payload = {
    redirect: detail.redirect || '/checkout',
    productId: detail.productId,
    cartHandles: detail.cartHandles,
    cartIds: detail.cartIds,
  };

  if (flowState.openHandler) {
    flowState.openHandler(payload);
    return;
  }

  flowState.pendingOpens.push(payload);
  document.dispatchEvent(new CustomEvent('checkout-upsell:open', { detail: payload }));

  // Handler henüz kayıtlı değilse kısa süre sonra SADECE kuyruğu boşalt.
  // Kuyruk zaten boşaltıldıysa tekrar openHandler(payload) çağırma (çift modal açılımı).
  window.setTimeout(() => {
    if (!flowState.openHandler) return;
    while (flowState.pendingOpens.length) {
      flowState.openHandler(flowState.pendingOpens.shift());
    }
  }, 50);
}

export function registerCheckoutUpsellHandler(handler) {
  flowState.openHandler = handler;
  while (flowState.pendingOpens.length) {
    handler(flowState.pendingOpens.shift());
  }
}

function getBuyNowQuantity(form) {
  const productForm = form.closest('product-form-component');
  const quantitySelector = productForm?.querySelector('quantity-selector-component');
  const fromSelector = quantitySelector?.getValue?.();
  if (fromSelector) return Number(fromSelector);

  const qtyInput = form.querySelector('[name="quantity"]');
  if (qtyInput instanceof HTMLInputElement && qtyInput.value) {
    return Number(qtyInput.value);
  }

  return 1;
}

function findProductForm(button) {
  if (button?.form instanceof HTMLFormElement) return button.form;
  const inComponent = button?.closest('product-form-component')?.querySelector('form');
  if (inComponent instanceof HTMLFormElement) return inComponent;
  return document.querySelector('product-form-component form[data-type="add-to-cart-form"]');
}

function getVariantId(form) {
  const input = form.querySelector('[name="id"]');
  if (input instanceof HTMLInputElement && input.value) return input.value;
  const productForm = form.closest('product-form-component');
  const fromComponent = productForm?.querySelector('[name="id"]');
  if (fromComponent instanceof HTMLInputElement && fromComponent.value) return fromComponent.value;
  return productForm?.dataset?.currentVariantId || '';
}

function resetBuyNowButton(button, originalText) {
  if (!(button instanceof HTMLButtonElement)) return;
  button.disabled = false;
  button.removeAttribute('data-conv-buy-now-busy');
  if (originalText) button.textContent = originalText;
}

async function fetchCartWithRetry(minItems = 1, attempts = 6) {
  for (let attempt = 0; attempt < attempts; attempt++) {
    const response = await fetch('/cart.js', { cache: 'no-store' });
    if (!response.ok) break;
    const cart = await response.json();
    if ((cart.items?.length || 0) >= minItems) return cart;
    await new Promise((resolve) => window.setTimeout(resolve, 100 + attempt * 50));
  }

  const response = await fetch('/cart.js', { cache: 'no-store' });
  return response.json();
}

function getCartAddAction() {
  const root = window.Shopify?.routes?.root || '/';
  return `${root}cart/add`;
}

function buildBuyNowCartAddUrl(form) {
  const variantId = getVariantId(form);
  if (!variantId) return '';

  const quantity = getBuyNowQuantity(form);
  const params = new URLSearchParams();
  params.set('id', String(variantId));
  params.set('quantity', String(quantity || 1));
  params.set('return_to', '/checkout');

  form.querySelectorAll('input[name^="properties["], textarea[name^="properties["], select[name^="properties["]').forEach((el) => {
    if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) return;
    if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
    if (!el.name || el.disabled || !el.value) return;
    params.append(el.name, el.value);
  });

  return `${getCartAddAction()}?${params.toString()}`;
}

/**
 * Upsell kapalıyken: /cart/add?return_to=/checkout URL'sine git.
 * <a> ise preventDefault YAPMA — tarayıcı kendi navigasyonunu kullansın.
 */
function buyNowViaFormRedirect(form, triggerButton = null) {
  const url = buildBuyNowCartAddUrl(form);
  if (!url) {
    console.warn('Buy now: variant id missing');
    return false;
  }

  if (triggerButton) {
    triggerButton.dataset.convBuyNowBusy = 'true';
    if ('disabled' in triggerButton) triggerButton.disabled = true;
    triggerButton.setAttribute('aria-disabled', 'true');
    triggerButton.textContent = 'Yönlendiriliyor…';
  }

  window.location.assign(url);
  return true;
}

export async function buyNowWithUpsell(form, triggerButton = null) {
  // Upsell yoksa fetch kullanma — doğrudan checkout'a git.
  if (!isCheckoutUpsellEnabled()) {
    return buyNowViaFormRedirect(form, triggerButton);
  }

  const variantId = getVariantId(form);
  if (!variantId) {
    console.warn('Buy now: variant id missing');
    return false;
  }

  if (triggerButton?.dataset.convBuyNowBusy === 'true') return false;

  const productForm = form.closest('product-form-component');
  const productId = productForm?.dataset?.productId;
  const quantity = getBuyNowQuantity(form);
  const originalText = triggerButton?.textContent?.trim() || '';

  if (triggerButton) {
    triggerButton.dataset.convBuyNowBusy = 'true';
    if ('disabled' in triggerButton) triggerButton.disabled = true;
    triggerButton.textContent = 'Yönlendiriliyor…';
  }

  try {
    const addBody = {
      items: [{ id: Number(variantId), quantity: Number(quantity) }],
    };

    const response = await fetch(`${getCartAddAction()}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(addBody),
    });

    const addData = await response.json().catch(() => null);
    if (!response.ok || addData?.status) {
      console.warn('Buy now: cart add failed', addData?.description || response.status);
      resetBuyNowButton(triggerButton, originalText);
      return false;
    }

    let cart = await fetchCartWithRetry(1);
    if (!(cart?.items?.length > 0)) {
      await new Promise((resolve) => window.setTimeout(resolve, 250));
      cart = await fetchCartWithRetry(1, 4);
    }

    if (!(cart?.items?.length > 0)) {
      console.warn('Buy now: cart empty after successful add response');
      resetBuyNowButton(triggerButton, originalText);
      return false;
    }

    const cartHandles = cart.items.map((item) => item.handle).join(',');
    const cartIds = cart.items.map((item) => String(item.product_id)).join(',');

    if (!hasUpsellRecommendations(cartHandles, cartIds)) {
      window.location.replace('/checkout');
      return true;
    }

    try {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: String(variantId),
            data: {
              source: 'buy-now',
              itemCount: Number(cart.item_count) || cart.items.length,
              cartCount: Number(cart.item_count) || cart.items.length,
              productId,
              variantId: String(variantId),
              sections: addData?.sections,
            },
          },
        })
      );
    } catch (error) {
      console.warn('Buy now: cart:update dispatch failed', error);
    }

    openCheckoutUpsell({
      redirect: '/checkout',
      productId,
      cartHandles,
      cartIds,
    });

    window.setTimeout(() => {
      resetBuyNowButton(triggerButton, originalText);
    }, 800);

    return true;
  } catch (error) {
    console.error('Buy now failed:', error);
    resetBuyNowButton(triggerButton, originalText);
    return false;
  }
}

function onBuyNowClick(event) {
  const button = event.target.closest('[data-conv-buy-now]');
  if (!button) return;
  if (button instanceof HTMLButtonElement && button.disabled) return;
  if (button.getAttribute('aria-disabled') === 'true') return;
  if (button.dataset.convBuyNowBusy === 'true') return;

  const form = findProductForm(button);
  if (!(form instanceof HTMLFormElement)) return;

  if (!getVariantId(form)) {
    console.warn('Buy now: variant id missing');
    return;
  }

  // Upsell kapalı: <a> ise href güncelle ve native navigasyona izin ver.
  if (!isCheckoutUpsellEnabled()) {
    const url = buildBuyNowCartAddUrl(form);
    if (!url) return;

    if (button instanceof HTMLAnchorElement) {
      button.href = url;
      button.dataset.convBuyNowBusy = 'true';
      button.textContent = 'Yönlendiriliyor…';
      // preventDefault YOK — tarayıcı linki takip eder
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    buyNowViaFormRedirect(form, button);
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  buyNowWithUpsell(form, button);
}

function bindBuyNowListeners() {
  if (flowState.listenersBound) return;
  flowState.listenersBound = true;
  document.addEventListener('click', onBuyNowClick, true);
}

/** Sepet → ödeme: doğrudan /checkout (form POST güvenilir değil). */
function goToCheckout() {
  window.location.replace('/checkout');
}

const CHECKOUT_TRIGGER_SELECTOR =
  'a.cart__checkout-button, a.cart-drawer__checkout, [name="checkout"].cart__checkout-button, [name="checkout"].cart-drawer__checkout';

function findCheckoutTrigger(target) {
  if (!(target instanceof Element)) return null;
  const el = target.closest(CHECKOUT_TRIGGER_SELECTOR);
  return el instanceof HTMLAnchorElement || el instanceof HTMLButtonElement ? el : null;
}

class CheckoutUpsellModal extends HTMLElement {
  /** @type {{ form?: HTMLFormElement | null, button?: HTMLButtonElement | null, redirect?: string } | null} */
  #pending = null;

  /** @type {number} */
  #loadGeneration = 0;

  /** @type {number} */
  #scrollLockCount = 0;

  connectedCallback() {
    if (this.dataset.enabled !== 'true') return;

    registerCheckoutUpsellHandler((detail) => this.#openWithUpsell(detail));
    this.addEventListener('click', this.#onClick);
    document.addEventListener('checkout-upsell:open', this.#onExternalOpen);
    document.addEventListener('click', this.#onCheckoutClick, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('checkout-upsell:open', this.#onExternalOpen);
    document.removeEventListener('click', this.#onCheckoutClick, true);
    this.#unlockScroll(true);
  }

  #onCheckoutClick = async (event) => {
    if (this.dataset.enabled !== 'true') return;

    const trigger = findCheckoutTrigger(/** @type {Element} */ (event.target));
    if (!trigger || this.contains(trigger)) return;
    if (trigger.dataset.skipUpsell === 'true') return;
    if (trigger instanceof HTMLButtonElement && trigger.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    const cartMeta = await this.#getCartMeta();
    if (!hasUpsellRecommendations(cartMeta.handles, cartMeta.ids)) {
      goToCheckout();
      return;
    }

    this.#pending = { redirect: '/checkout' };
    await this.#open(cartMeta.handles, cartMeta.ids);
  };

  #lockScroll() {
    this.#scrollLockCount += 1;
    document.documentElement.classList.add('overflow-hidden');
  }

  #unlockScroll(force = false) {
    this.#scrollLockCount = force ? 0 : Math.max(0, this.#scrollLockCount - 1);
    if (this.#scrollLockCount === 0) {
      document.documentElement.classList.remove('overflow-hidden');
    }
  }

  #onExternalOpen = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    this.#openWithUpsell(detail);
  };

  async #openWithUpsell(detail) {
    let cartHandles = detail.cartHandles || '';
    let cartIds = detail.cartIds || '';

    if (!cartHandles) {
      const cartMeta = await this.#getCartMeta();
      cartHandles = cartMeta.handles;
      cartIds = cartMeta.ids;
    }

    if (!hasUpsellRecommendations(cartHandles, cartIds)) {
      this.#close();
      if (detail.redirect) window.location.assign(detail.redirect);
      return;
    }

    if (detail.productId) {
      this.dataset.productId = String(detail.productId);
    }

    this.#pending = { redirect: detail.redirect || '/checkout' };
    await this.#open(cartHandles, cartIds);
  }

  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (target.closest('[data-close]')) {
      event.preventDefault();
      this.#proceedCheckout();
      return;
    }

    if (target.closest('[data-continue]')) {
      event.preventDefault();
      this.#proceedCheckout();
      return;
    }

    const addButton = target.closest('.checkout-upsell-modal__add');
    if (addButton instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#addToCart(addButton);
    }
  };

  async #open(cartHandles = '', cartIds = '') {
    this.classList.remove('hidden');
    this.#lockScroll();

    const container = this.querySelector('[data-products]');
    if (!(container instanceof HTMLElement)) {
      this.#close();
      return;
    }

    const openGeneration = ++this.#loadGeneration;
    container.innerHTML = '<p class="checkout-upsell-modal__loading">Ürünler yükleniyor…</p>';

    try {
      const html = await this.#loadProducts(cartHandles, cartIds, openGeneration);

      // Daha yeni bir açılış başladıysa bu sonucu yut (scroll kilidini bırakma — yeni açılış sahibi).
      if (openGeneration !== this.#loadGeneration) return;

      if (!html) {
        // Öneri yok / yüklenemedi → kullanıcıyı ekstra tıklamaya zorlama, ödemeye geç.
        this.#proceedCheckout();
        return;
      }

      container.innerHTML = html;
    } catch (error) {
      if (openGeneration !== this.#loadGeneration) return;
      console.warn('Checkout upsell open:', error);
      this.#proceedCheckout();
    }
  }

  #close() {
    this.classList.add('hidden');
    this.#unlockScroll(true);
  }

  async #loadProducts(cartHandles, cartIds, generation) {
    let handles = cartHandles;
    let ids = cartIds;

    if (!handles) {
      const cartMeta = await this.#getCartMeta();
      handles = cartMeta.handles;
      ids = cartMeta.ids;
    }

    const html = await fetchUpsellForCart(handles, ids, 'modal');
    if (generation !== this.#loadGeneration) return null;
    return html || '';
  }

  async #getCartMeta() {
    try {
      const res = await fetch('/cart.js', { cache: 'no-store' });
      if (!res.ok) throw new Error('cart unavailable');
      const cart = await res.json();
      return {
        handles: cart.items.map((item) => item.handle).join(','),
        ids: cart.items.map((item) => String(item.product_id)).join(','),
      };
    } catch {
      return {
        handles: this.dataset.cartHandles || '',
        ids: this.dataset.cartIds || '',
      };
    }
  }

  async #addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    const originalText = button.textContent?.trim() || 'Sepete Ekle';
    button.disabled = true;
    button.textContent = '...';

    const sectionIds = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
        }),
      });
      const data = await response.json();

      if (data.status) {
        button.textContent = 'Hata';
        window.setTimeout(() => {
          button.textContent = originalText;
        }, 1500);
        return;
      }

      button.textContent = 'Eklendi';
      button.classList.add('is-added');

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: variantId,
            data: {
              source: 'checkout-upsell-modal',
              itemCount: Number(data.item_count) || 1,
              cartCount: Number(data.item_count) || 1,
              productId: button.dataset.productId,
              variantId,
              sections: data.sections,
            },
          },
        })
      );

      window.setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove('is-added');
      }, 1500);
    } catch (error) {
      console.warn('Checkout upsell add:', error);
      button.textContent = originalText;
    } finally {
      button.disabled = false;
    }
  }

  #proceedCheckout() {
    this.#close();
    this.#pending = null;
    goToCheckout();
  }
}

function initCheckoutUpsellModal() {
  if (!customElements.get('checkout-upsell-modal')) {
    customElements.define('checkout-upsell-modal', CheckoutUpsellModal);
  }
}

bindBuyNowListeners();
if (document.querySelector('checkout-upsell-modal')) {
  initCheckoutUpsellModal();
}
